<template>
  <div class="access-dashboard" v-if="autorizado">
    <div class="page-header">
      <h2><v-icon class="mr-1" color="primary">mdi-chart-box-outline</v-icon> Dashboard de accesos</h2>
    </div>

    <!-- Filtro de rango -->
    <div class="filtros">
      <div class="presets">
        <v-btn
          v-for="preset in presets"
          :key="preset.dias"
          size="small"
          :variant="presetActivo === preset.dias ? 'flat' : 'tonal'"
          color="primary"
          @click="aplicarPreset(preset.dias)"
        >
          {{ preset.label }}
        </v-btn>
      </div>
      <div class="fechas">
        <v-text-field
          v-model="from"
          type="date"
          label="Desde"
          variant="outlined"
          density="compact"
          hide-details
          @change="presetActivo = null; cargarStats()"
        />
        <v-text-field
          v-model="to"
          type="date"
          label="Hasta"
          variant="outlined"
          density="compact"
          hide-details
          @change="presetActivo = null; cargarStats()"
        />
      </div>
    </div>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
      {{ error }}
    </v-alert>

    <div v-if="cargando" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else-if="stats">
      <!-- KPIs -->
      <div class="kpi-grid">
        <div class="kpi-tile">
          <v-icon size="22" color="primary">mdi-eye-outline</v-icon>
          <div class="kpi-value">{{ stats.total }}</div>
          <div class="kpi-label">Visitas totales</div>
        </div>
        <div class="kpi-tile">
          <v-icon size="22" color="primary">mdi-map-marker-radius-outline</v-icon>
          <div class="kpi-value">{{ stats.uniqueIp }}</div>
          <div class="kpi-label">Visitantes únicos (IP)</div>
        </div>
        <div class="kpi-tile">
          <v-icon size="22" color="primary">mdi-incognito</v-icon>
          <div class="kpi-value">{{ stats.anonymous }}</div>
          <div class="kpi-label">Anónimos</div>
        </div>
        <div class="kpi-tile">
          <v-icon size="22" color="primary">mdi-account-check-outline</v-icon>
          <div class="kpi-value">{{ stats.registered }}</div>
          <div class="kpi-label">Logueados</div>
        </div>
      </div>

      <!-- Anónimos vs Logueados -->
      <div class="proporcion" v-if="stats.total > 0">
        <div class="proporcion-titulo">Anónimos vs. logueados</div>
        <div class="split-bar">
          <div class="split-segment seg-anon" :style="{ width: anonPct + '%' }" />
          <div class="split-segment seg-reg" :style="{ width: regPct + '%' }" />
        </div>
        <div class="split-legend">
          <span><span class="dot dot-anon" /> Anónimos — {{ stats.anonymous }} ({{ anonPct }}%)</span>
          <span><span class="dot dot-reg" /> Logueados — {{ stats.registered }} ({{ regPct }}%)</span>
        </div>
      </div>
      <div v-else class="empty-state">
        <v-icon size="42" color="grey-lighten-1">mdi-chart-box-outline</v-icon>
        <p class="text-grey mt-2">Sin accesos registrados en este rango.</p>
      </div>

      <!-- Usuarios logueados -->
      <div class="ip-tabla" v-if="userPage.content.length > 0">
        <div class="proporcion-titulo">Movimientos de usuarios logueados</div>
        <div class="tabla-scroll">
          <v-table density="compact" hover>
            <thead>
              <tr>
                <th>Usuario</th>
                <th class="text-right">Visitas</th>
                <th>Primera vez</th>
                <th>Última vez</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in userPage.content" :key="item.idUser">
                <td>
                  <div class="user-cell">{{ item.fullName || item.username || ('#' + item.idUser) }}</div>
                  <div class="user-sub" v-if="item.username">@{{ item.username }}</div>
                </td>
                <td class="text-right">
                  <v-chip size="x-small" color="primary" variant="tonal">{{ item.total }}</v-chip>
                </td>
                <td>{{ formatFechaHora(item.firstAccess) }}</td>
                <td>{{ formatFechaHora(item.lastAccess) }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <div class="paginacion" v-if="userPage.totalPages > 1">
          <v-pagination
            v-model="userPageNumber"
            :length="userPage.totalPages"
            density="comfortable"
            active-color="primary"
            @update:model-value="cambiarPaginaUser"
          />
        </div>
      </div>

      <!-- IPs repetidas -->
      <div class="ip-tabla" v-if="ipPage.content.length > 0">
        <div class="proporcion-titulo">IPs con más accesos</div>
        <div class="tabla-scroll">
          <v-table density="compact" hover>
            <thead>
              <tr>
                <th>IP</th>
                <th class="text-right">Visitas</th>
                <th>Primera vez</th>
                <th>Última vez</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in ipPage.content" :key="item.ip">
                <td class="ip-cell">{{ item.ip }}</td>
                <td class="text-right">
                  <v-chip size="x-small" color="primary" variant="tonal">{{ item.total }}</v-chip>
                </td>
                <td>{{ formatFechaHora(item.firstAccess) }}</td>
                <td>{{ formatFechaHora(item.lastAccess) }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <div class="paginacion" v-if="ipPage.totalPages > 1">
          <v-pagination
            v-model="ipPageNumber"
            :length="ipPage.totalPages"
            density="comfortable"
            active-color="primary"
            @update:model-value="cambiarPaginaIp"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import apisUser from "@/apis/apisUsers";
import apisAccessRecords from "@/apis/apisAccessRecords";

export default {
  name: "AccessDashboardView",
  data() {
    return {
      autorizado: false,
      from: "",
      to: "",
      presetActivo: 7,
      presets: [
        { label: "Hoy", dias: 0 },
        { label: "7 días", dias: 7 },
        { label: "30 días", dias: 30 },
      ],
      stats: null,
      ipPage: { content: [], totalPages: 0 },
      ipPageNumber: 1,
      userPage: { content: [], totalPages: 0 },
      userPageNumber: 1,
      cargando: false,
      error: null,
    };
  },
  computed: {
    anonPct() {
      if (!this.stats || this.stats.total === 0) return 0;
      return Math.round((this.stats.anonymous / this.stats.total) * 100);
    },
    regPct() {
      if (!this.stats || this.stats.total === 0) return 0;
      return 100 - this.anonPct;
    },
    desdeIso() {
      return `${this.from}T00:00:00`;
    },
    hastaIso() {
      return `${this.to}T23:59:59`;
    },
  },
  async created() {
    const isValid = await apisUser.isValid().catch(() => false);
    if (!isValid) {
      this.$router.replace({ name: "LoginView" });
      return;
    }
    const user = await apisUser.GetMe().catch(() => null);
    if (!user || user.role !== "administrador") {
      this.$router.replace({ name: "home" });
      return;
    }

    this.autorizado = true;
    this.aplicarPreset(7);
  },
  methods: {
    formatearFecha(date) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    },
    formatFechaHora(isoString) {
      if (!isoString) return "-";
      const d = new Date(isoString);
      return d.toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" });
    },
    aplicarPreset(dias) {
      this.presetActivo = dias;
      const hoy = new Date();
      const desde = new Date();
      desde.setDate(hoy.getDate() - dias);
      this.to = this.formatearFecha(hoy);
      this.from = this.formatearFecha(desde);
      this.cargarStats();
    },
    async cargarStats() {
      if (!this.from || !this.to) return;
      this.cargando = true;
      this.error = null;
      this.ipPageNumber = 1;
      this.userPageNumber = 1;
      try {
        const [stats, ipPage, userPage] = await Promise.all([
          apisAccessRecords.getStats(this.desdeIso, this.hastaIso),
          apisAccessRecords.getByIp(this.desdeIso, this.hastaIso, 0),
          apisAccessRecords.getByUser(this.desdeIso, this.hastaIso, 0),
        ]);
        this.stats = stats;
        this.ipPage = ipPage;
        this.userPage = userPage;
      } catch (e) {
        this.error = e.response?.data || "No se pudieron cargar las estadísticas";
        this.stats = null;
        this.ipPage = { content: [], totalPages: 0 };
        this.userPage = { content: [], totalPages: 0 };
      } finally {
        this.cargando = false;
      }
    },
    async cambiarPaginaIp(pagina) {
      this.ipPage = await apisAccessRecords.getByIp(this.desdeIso, this.hastaIso, pagina - 1);
    },
    async cambiarPaginaUser(pagina) {
      this.userPage = await apisAccessRecords.getByUser(this.desdeIso, this.hastaIso, pagina - 1);
    },
  },
};
</script>

<style scoped>
.access-dashboard {
  max-width: 700px;
  margin: 0 auto;
  padding: 16px;
}

.page-header {
  margin-bottom: 16px;
}

.page-header h2 {
  font-size: 1.3rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.filtros {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.fechas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}

@media screen and (min-width: 600px) {
  .kpi-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.kpi-tile {
  background: #fff;
  border: 1px solid #e1e0d9;
  border-radius: 10px;
  padding: 14px 10px;
  text-align: center;
}

.kpi-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #0b0b0b;
  line-height: 1.3;
  margin-top: 4px;
}

.kpi-label {
  font-size: 0.78rem;
  color: #898781;
}

.proporcion {
  background: #fff;
  border: 1px solid #e1e0d9;
  border-radius: 10px;
  padding: 14px;
}

.proporcion-titulo {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: #52514e;
}

.split-bar {
  display: flex;
  width: 100%;
  height: 14px;
  border-radius: 4px;
  overflow: hidden;
  gap: 2px;
  background: #f0efec;
}

.split-segment {
  height: 100%;
}

.seg-anon {
  background: #1baf7a;
  border-radius: 4px 0 0 4px;
}

.seg-reg {
  background: #2a78d6;
  border-radius: 0 4px 4px 0;
}

.split-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 10px;
  font-size: 0.82rem;
  color: #52514e;
}

.dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 5px;
}

.dot-anon {
  background: #1baf7a;
}

.dot-reg {
  background: #2a78d6;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px;
  text-align: center;
}

.ip-tabla {
  background: #fff;
  border: 1px solid #e1e0d9;
  border-radius: 10px;
  padding: 14px;
  margin-top: 16px;
}

.tabla-scroll {
  overflow-x: auto;
}

.ip-cell {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.user-cell {
  font-weight: 500;
  white-space: nowrap;
}

.user-sub {
  font-size: 0.72rem;
  color: #898781;
}

.paginacion {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}
</style>
